import { httpsCallable } from 'firebase/functions';
import { auth, functions } from './firebase';
import { openTabCount } from './pageTabs';

/**
 * 화면 멈춤 기록(2026-09-29 속도 3차 — 「마우스가 느려졌다」).
 *
 * 개발 PC 에선 0.2초 넘는 멈춤이 0건이라 **사용자 PC 에서 모은다.** 멈춤마다 화면 경로·직전 클릭·
 * 원인 스크립트(지원 브라우저만)를 모아 **1분에 한 번** 서버 로그로 보낸다(`functions/src/perfLog.ts`).
 * 🔴 기록이 또 다른 멈춤이 되지 않게: 관찰자 콜백은 배열에 넣기만 하고, 전송은 한가할 때 한 번에.
 *    로딩 카드·조회 캐시를 거치지 않도록 `api.ts call()` 이 아니라 SDK 를 바로 부른다.
 */
const MIN_MS = 200;
const MAX_BUF = 30;

type Script = { fn: string | null; src: string | null; inv: string | null; d: number };
/**
 * layout = 프레임 끝 스타일·배치 시간 · forced = 스크립트가 강제한 배치 합 · nodes = 그때 DOM 요소 수 · tabs = 열린 화면 탭 수.
 * 10-20 집계에서 배차 탭 전환 0.4~0.9초(ilyang)가 스크립트 100~270ms 뿐이라 나머지가 배치인지·DOM·탭 수 탓인지 못 갈랐다.
 */
type Entry = {
  path: string; dur: number; block: number | null; act: string | null; scripts: Script[];
  layout: number | null; forced: number | null; nodes: number; tabs: number;
};

let buf: Entry[] = [];
let lastAct: { label: string; at: number } | null = null;

/**
 * alert·confirm·prompt 가 떠 있던 구간. 그 창은 사람이 「확인」을 누를 때까지 화면을 붙잡아
 * long-animation-frame 에 **사람이 읽은 시간**이 멈춤으로 잡힌다(10-01 실측: 정산 「네, 1건 등록합니다」 18건 ·
 * 중앙 1.2초 · 최대 3초 = 등록 뒤 alert). 겹친 만큼 빼고, 남은 게 기준 미만이면 기록하지 않는다.
 */
const dialogs: Array<[number, number]> = [];
function wrapDialogs() {
  for (const k of ['alert', 'confirm', 'prompt'] as const) {
    const orig = window[k] as (...a: unknown[]) => unknown;
    (window as unknown as Record<string, unknown>)[k] = (...a: unknown[]) => {
      const t0 = performance.now();
      try {
        return orig.apply(window, a);
      } finally {
        dialogs.push([t0, performance.now()]);
        if (dialogs.length > 20) dialogs.shift();
      }
    };
  }
}
function dialogOverlap(start: number, end: number): number {
  let ms = 0;
  for (const [a, b] of dialogs) ms += Math.max(0, Math.min(b, end) - Math.max(a, start));
  return ms;
}

/** 누른 것의 짧은 이름 — 버튼 글자·aria-label·태그. 입력값은 담지 않는다(개인정보). */
function labelOf(t: EventTarget | null): string {
  const el = t instanceof Element ? (t.closest('button, a, [role], th, td, label, select, input') ?? t) : null;
  if (!el) return '?';
  const aria = el.getAttribute('aria-label');
  const tag = el.tagName.toLowerCase();
  if (tag === 'input' || tag === 'select') return `${tag}:${el.getAttribute('name') ?? el.getAttribute('placeholder') ?? ''}`.slice(0, 40);
  const text = (aria ?? el.textContent ?? '').replace(/\s+/g, ' ').trim().slice(0, 30);
  return `${tag}:${text}`;
}

const base = (u: unknown): string | null => (typeof u === 'string' && u ? (u.split('/').pop() ?? '').split('?')[0] || null : null);

function record(e: PerformanceEntry & { blockingDuration?: number; scripts?: unknown[]; styleAndLayoutStart?: number }) {
  if (e.duration < MIN_MS || document.visibilityState !== 'visible' || buf.length >= MAX_BUF) return;
  const wait = dialogOverlap(e.startTime, e.startTime + e.duration);
  const dur = e.duration - wait;
  if (dur < MIN_MS) return;
  const all = (e.scripts ?? []) as Array<{ duration: number; sourceFunctionName?: string; sourceURL?: string; invoker?: string; forcedStyleAndLayoutDuration?: number }>;
  // 1초 박자 · 막힘 0 · 스크립트 거의 없음 = 브라우저가 늦춘 탭(자동화·가려진 창)의 프레임 대기 — 멈춤이 아니다
  // (10-20 실측 177건 중 175건 way PC). `perf-summary.mjs throttled` 와 같은 판정.
  if (dur >= 900 && dur <= 1100 && e.blockingDuration === 0 && all.reduce((s, x) => s + x.duration, 0) < 100) return;
  const scripts = all
    .slice().sort((a, b) => b.duration - a.duration).slice(0, 3)
    .map((s) => ({ fn: s.sourceFunctionName || null, src: base(s.sourceURL), inv: s.invoker?.slice(0, 80) ?? null, d: Math.round(s.duration) }));
  buf.push({
    path: location.pathname,
    dur: Math.round(dur),
    block: typeof e.blockingDuration === 'number' ? Math.max(0, Math.round(e.blockingDuration - wait)) : null,
    // 멈춤 시작 2초 안의 마지막 클릭·키 입력 — 「무엇을 하다 멈췄나」
    act: lastAct && e.startTime - lastAct.at < 2000 && e.startTime >= lastAct.at - 50 ? lastAct.label : null,
    scripts,
    layout: e.styleAndLayoutStart ? Math.round(e.startTime + e.duration - e.styleAndLayoutStart) : null,
    forced: all.length ? Math.round(all.reduce((s, x) => s + (x.forcedStyleAndLayoutDuration ?? 0), 0)) : null,
    nodes: document.getElementsByTagName('*').length,
    tabs: openTabCount(),
  });
}

function flush() {
  if (!buf.length || !auth.currentUser) return;
  const entries = buf;
  buf = [];
  const nav = navigator as Navigator & { deviceMemory?: number };
  httpsCallable(functions, 'logPerf')({
    entries, cores: nav.hardwareConcurrency ?? null, mem: nav.deviceMemory ?? null,
    ua: nav.userAgent, ver: typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : null,
  }).catch(() => { /* 기록 실패는 화면에 알리지 않는다 */ });
}

export function startPerfWatch(): void {
  if (typeof PerformanceObserver === 'undefined') return;
  const types = PerformanceObserver.supportedEntryTypes ?? [];
  // long-animation-frame(크롬 123+)은 원인 스크립트까지 준다. 없으면 longtask(시간만).
  const type = types.includes('long-animation-frame') ? 'long-animation-frame' : types.includes('longtask') ? 'longtask' : null;
  if (!type) return;
  wrapDialogs();
  const mark = (ev: Event) => { lastAct = { label: labelOf(ev.target), at: performance.now() }; };
  addEventListener('pointerdown', mark, { capture: true, passive: true });
  addEventListener('keydown', mark, { capture: true, passive: true });
  try {
    new PerformanceObserver((list) => list.getEntries().forEach((e) => record(e as never))).observe({ type, buffered: false });
  } catch {
    return;
  }
  setInterval(flush, 60_000);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') flush(); });
}
